import React, { useState, useCallback } from "react";
import { toast } from "react-toastify";
import AssignSchools from "./AssignSchools";
import VisitsReports from "./VisitsReports";
import { get, post, del } from "../../services/api";

function QAVisitManagement() {
  const [activeTab, setActiveTab] = useState("assign");
  const [assignments, setAssignments] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [qaUsers, setQaUsers] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  const [centers, setCenters] = useState([]);
  const [qaRoleId, setQaRoleId] = useState(0);
  const [qaDepartmentId, setQaDepartmentId] = useState(0);
  const [priorities, setPriorities] = useState([]);
  
  const [stats, setStats] = useState({
    totalAssignments: 0,
    totalQaUsers: 0,
    totalCenters: 0,
    pendingVisits: 0,
    completedVisits: 0,
  });
  
  const organizationId = localStorage.getItem("organizationId") || 0;
  const token = localStorage.getItem("token");

  // Fetch QA Configuration (Role and Department IDs)
  const fetchQAConfig = useCallback(async () => {
    try {
      // 1. Fetch Role ID for "QA Officer"
      const rolesResponse = await get("Role", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const qaRole = rolesResponse?.find(r => 
        r.name.toLowerCase().includes("qa") && r.name.toLowerCase().includes("officer")
      ) || rolesResponse?.find(r => r.name.toLowerCase().includes("qa")); // Fallback to any QA role
      
      if (qaRole) {
        setQaRoleId(qaRole.id);
      }

      // 2. Fetch Department ID for "QA"
      const deptResponse = await get(
        `CommonSettings/GetCommonSettingDetailByModuleName?moduleName=Department`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const qaDept = deptResponse?.commonSettingDetail?.find(d => 
        d.value.toLowerCase() === "qa" || d.value.toLowerCase().includes("quality assurance")
      );
      
      if (qaDept) {
        setQaDepartmentId(qaDept.id);
      }
    } catch (error) {
      console.error("Error fetching QA configuration:", error);
    }
  }, [token]);

  // Fetch Provinces
  const fetchProvinces = useCallback(async () => {
    try {
      const response = await get(
        `Dashboard/GetSatateByOrganizationId?organizationId=${organizationId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProvinces(response || []);
    } catch (error) {
      console.error("Error fetching provinces:", error);
    }
  }, [organizationId, token]);

  // Fetch Cities by Province
  const fetchCities = useCallback(async (provinceCode) => {
    if (!provinceCode) return;
    try {
      const response = await get(`Dashboard/GetCityByStateCode?code=${provinceCode}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCities(response || []);
      setCenters([]); // Clear centers when cities change
    } catch (error) {
      console.error("Error fetching cities:", error);
    }
  }, [token]);

  // Fetch Centers by City
  const fetchCenters = useCallback(async (cityCode) => {
    if (!cityCode) return;
    try {
      const response = await get(
        `Dashboard/GetCenterByCityCode?code=${cityCode}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCenters(response || []);
    } catch (error) {
      console.error("Error fetching centers:", error);
    }
  }, [token]);

  // Fetch QA Users
  const fetchQAUsers = useCallback(async () => {
    if (!qaRoleId) return; // Wait for roleId to be fetched
    try {
      // If departmentId is not found (0), we still fetch by roleId
      const response = await get(
        `User/GetUsersByRoleIdAndDepartmentId?roleId=${qaRoleId}&departmentId=${qaDepartmentId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setQaUsers(response || []);
    } catch (error) { 
      console.error("Error fetching QA users:", error);
    }
  }, [token, qaRoleId, qaDepartmentId]);

  // Fetch Assignments List
  const fetchAssignments = useCallback(async () => {
    try {
      const response = await get("QAVisit/assignments", {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const rawData = Array.isArray(response) ? response : (response?.data || []);
      
      // Normalize data to match frontend keys: qaUserId, createdAt, etc.
      const normalizedData = rawData.map(a => ({
        id: a.id,
        qaUserId: a.userId || a.qaUserId, // Map userId to qaUserId
        qaUserName: a.userName || a.qaUserName, // Store the name from API as fallback
        centerId: a.centerId || a.campusId, // Map centerId or campusId
        centerName: a.centerName || a.campusName, // Store the name from API as fallback
        notes: a.notes,
        // Map any common date field or fallback to current local string
        createdAt: a.createdAt || a.createdOn || a.assignedOn || a.assignedDate || new Date().toLocaleString(),
        // New mappings based on API response
        priority: a.priority,
        visitDate: a.scheduledDate ? a.scheduledDate.split('T')[0] : null,
        isActive: a.isActive
      }));
      
      setAssignments(normalizedData);

      // Derive schedules from assignments for UI compatibility
      const derivedSchedules = normalizedData.filter(a => a.visitDate).map(a => ({
        id: a.id, // Use assignment ID as schedule ID for now or a.id
        qaUserId: a.qaUserId,
        centerId: a.centerId,
        visitDate: a.visitDate,
        priority: a.priority,
        status: "Pending", // Default to Pending if API doesn't provide status
        isActive: a.isActive
      }));
      setSchedules(derivedSchedules);

    } catch (error) {
      console.error("Error fetching assignments:", error);
    }
  }, [token]);
  
  // Fetch Priority Settings
  const fetchPriorities = useCallback(async () => {
    try {
      const response = await get(
        `CommonSettings/GetCommonSettingDetailByModuleName?moduleName=qaPriority`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPriorities(response?.commonSettingDetail || []);
    } catch (error) {
      console.error("Error fetching priorities:", error);
    }
  }, [token]);

  React.useEffect(() => {
    fetchProvinces();
    fetchQAConfig();
    fetchAssignments();
    fetchPriorities();
  }, [fetchProvinces, fetchQAConfig, fetchAssignments, fetchPriorities]);

  React.useEffect(() => {
    if (qaRoleId) {
      fetchQAUsers();
    }
  }, [qaRoleId, fetchQAUsers]);

  // Calculate stats whenever assignments or schedules change
  React.useEffect(() => {
    setStats(prev => ({
      ...prev,
      totalAssignments: assignments.length,
      totalQaUsers: qaUsers.length,
      totalCenters: centers.length,
      pendingVisits: schedules.filter(s => s.status === "Pending").length,
      completedVisits: schedules.filter(s => s.status === "Completed").length,
    }));
  }, [assignments, schedules, centers, qaUsers]);


  // Assignment Functions
  const handleAddAssignment = useCallback(async (newAssignment) => {
    try {
      // Find priority ID from the value string (if priority is selected)
      const priorityObj = priorities.find(p => p.value === newAssignment.priority);
      const priorityId = priorityObj ? priorityObj.id : null;

      const payload = {
        userId: Number(newAssignment.qaUserId),
        centerId: Number(newAssignment.centerId),
        notes: newAssignment.notes,
        // Additional parameters as requested
        OrganizationId: Number(organizationId),
        ScheduledDate: newAssignment.visitDate,
        PriorityId: priorityId,
        Notes: newAssignment.notes
      };

      const response = await post("QAVisit/assign-campus", payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response && response.isSuccess) {
        toast.success(response.message || "Center assigned successfully");
        
        // Auto-create schedule if visit date provided
        if (newAssignment.visitDate) {
          const scheduleData = {
            qaUserId: newAssignment.qaUserId,
            centerId: newAssignment.centerId,
            visitDate: newAssignment.visitDate,
            priority: newAssignment.priority || "Normal",
            instructions: newAssignment.notes, // Reuse notes as instructions
            status: "Pending"
          };
          handleAddSchedule(scheduleData);
        }

        fetchAssignments(); // Re-fetch to get real database IDs and synchronized state
        return true;
      } else {
        toast.error(response?.message || "Failed to assign center");
        return false;
      }
    } catch (error) {
      console.error("Error assigning campus:", error);
      toast.error("An error occurred during assignment");
      return false;
    }
  }, [assignments.length, token, priorities, organizationId]);

  const handleDeleteAssignment = useCallback(async (assignmentId) => {
    try {
      const response = await get(`QAVisit/DeleteAssignments?id=${assignmentId}`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response && response.isSuccess) {
        toast.success(response.message || "Assignment removed successfully");
        fetchAssignments(); // Re-fetch to stay synchronized with server
        return true;
      } else {
        toast.error(response?.message || "Failed to remove assignment");
        return false;
      }
    } catch (error) {
      console.error("Error deleting assignment:", error);
      toast.error("An error occurred while deleting the assignment");
      return false;
    }
  }, [token]);

  // Schedule Functions
  const handleAddSchedule = useCallback((newSchedule) => {
    const newId = `SCH-${String(schedules.length + 1).padStart(4, '0')}`;
    const scheduleWithId = {
      ...newSchedule,
      id: newId,
      status: "Pending",
      actualVisitDate: "",
      timeIn: "",
      timeOut: "",
      gpsIn: "",
      gpsOut: "",
      pdfKey: ""
    };
    
    setSchedules(prev => [...prev, scheduleWithId]);
    // toast.success("Visit scheduled successfully"); // Suppress toast if auto-created
    return true;
  }, [schedules.length]);

  const handleStartVisit = useCallback((scheduleId) => {
    setSchedules(prev =>
      prev.map(s => s.id === scheduleId ? {
        ...s,
        status: "In Progress",
        timeIn: new Date().toLocaleString(),
        gpsIn: "Captured from device",
        actualVisitDate: new Date().toISOString().split('T')[0],
      } : s)
    );
    toast.success("Visit started successfully");
  }, []);

  const handleCompleteVisit = useCallback((scheduleId) => {
    setSchedules(prev =>
      prev.map(s => s.id === scheduleId ? {
        ...s,
        status: "Completed",
        timeOut: new Date().toLocaleString(),
        gpsOut: "Captured from device",
        pdfKey: `QA-REPORT-${Date.now()}`,
      } : s)
    );
    toast.success("Visit completed successfully");
  }, []);

  const handleDeleteSchedule = useCallback((scheduleId) => {
    setSchedules(prev => prev.filter(s => s.id !== scheduleId));
    toast.success("Schedule deleted");
    return true;
  }, []);

  

  return (
    <div className="" style={{ backgroundColor: '#f3f5f7', minHeight: '100vh' }}>
      <div className="card shadow-sm" style={{ 
        borderRadius: '10px', 
        border: '1px solid #dee2e6',
        overflow: 'hidden'
      }}>
        {/* Card Header */}
        <div className="card-header" style={{ 
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)', 
          borderBottom: '1px solid #e0e6ed',
          padding: '1.5rem 1.75rem'
        }}>
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center gap-3">
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.25rem',
                boxShadow: '0 4px 8px rgba(79, 70, 229, 0.2)'
              }}>
                <i className="fas fa-clipboard-check"></i>
              </div>
              <div>
                <div className="page-title" style={{ 
                  fontSize: '1.5rem', 
                  fontWeight: 700, 
                  letterSpacing: '-0.01em',
                  color: '#1e293b',
                  marginBottom: '4px'
                }}>
                  QA Visit Management Module
                </div>
                <div className="text-muted" style={{ 
                  fontSize: '0.875rem',
                  color: '#64748b'
                }}>
                  Assign centers to QA users, schedule QA visits, and manage visit reports (PDF download demo).
                </div>
              </div>
            </div>
            <div className="d-flex align-items-center gap-1">
              <button 
                className="toolbar-icon-btn" 
                title="Refresh" 
                onClick={() => {
                  fetchAssignments();
                  toast.info("Data refreshed successfully");
                }}
                style={{
                  border: 'none',
                  background: '#f1f5f9',
                  borderRadius: '8px',
                  width: '40px',
                  height: '40px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  color: '#64748b'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#e2e8f0';
                  e.currentTarget.style.color = '#4f46e5';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#f1f5f9';
                  e.currentTarget.style.color = '#64748b';
                }}
              >
                <i className="fas fa-sync-alt"></i>
              </button>
              <button 
                className="toolbar-icon-btn" 
                title="Export (dummy)" 
                onClick={() => toast.info("Export is UI-only in this demo.")}
                style={{
                  border: 'none',
                  background: '#f1f5f9',
                  borderRadius: '8px',
                  width: '40px',
                  height: '40px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  color: '#64748b'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#e2e8f0';
                  e.currentTarget.style.color = '#4f46e5';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#f1f5f9';
                  e.currentTarget.style.color = '#64748b';
                }}
              >
                <i className="fas fa-file-export"></i>
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <ul className="nav nav-tabs px-3 pt-3" id="mainTabs" role="tablist">
          <li className="nav-item" role="presentation">
            <button
              className={`nav-link ${activeTab === "assign" ? "active" : ""}`}
              style={{
                borderRadius: 0,
                fontSize: '.9rem',
                padding: '.55rem 1.25rem',
                borderTop: activeTab === "assign" ? '3px solid #5212b1ff' : 'none',
                fontWeight: activeTab === "assign" ? 700 : 'normal'
              }}
              onClick={() => setActiveTab("assign")}
            >
              {/* <i className="fas fa-school mr-2"></i> */}
              {/* <i className="fas fa-school mr-2"></i> */}
              1) Assign Schools & Schedule
            </button>
          </li>
          <li className="nav-item" role="presentation">
            <button
              className={`nav-link ${activeTab === "visits" ? "active" : ""}`}
              style={{
                borderRadius: 0,
                fontSize: '.9rem',
                padding: '.55rem 1.25rem',
                borderTop: activeTab === "visits" ? '3px solid #5212b1ff' : 'none',
                fontWeight: activeTab === "visits" ? 700 : 'normal'
              }}
              onClick={() => setActiveTab("visits")}
            >
              {/* <i className="fas fa-file-alt mr-2"></i> */}
              2) Visits & Reports
            </button>
          </li>
        </ul>

        {/* Tab Content */}
        <div className="tab-content p-3">
          <div className={`tab-pane fade ${activeTab === "assign" ? "show active" : ""}`}>
            {activeTab === "assign" && (
              <AssignSchools
                assignments={assignments}
                qaUsers={qaUsers}
                centers={centers}
                provinces={provinces}
                cities={cities}
                stats={stats}
                priorities={priorities}
                schedules={schedules}
                onFetchCities={fetchCities}
                onFetchCenters={fetchCenters}
                onAddAssignment={handleAddAssignment}
                onDeleteAssignment={handleDeleteAssignment}
                onStartVisit={handleStartVisit}
                onCompleteVisit={handleCompleteVisit}
              />
            )}
          </div>
          <div className={`tab-pane fade ${activeTab === "visits" ? "show active" : ""}`}>
            {activeTab === "visits" && (
              <VisitsReports
                qaUsers={qaUsers}
                centers={centers}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default QAVisitManagement;